import { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { SectionTitle, Loading } from "@/components/common";
import { toast } from "sonner";
import { getRegisterOptions, createRegistrationCredential, finishRegisterPasskey, getStepUpOptions, getAssertion, finishStepUp, passkeySupported } from "@/lib/webauthn";
import { PasswordInput } from "@/components/PasswordInput";
import { ShieldCheck, KeyRound, Fingerprint, Smartphone, RefreshCw, Trash2, Plus, X, Loader2, Copy } from "lucide-react";

function StepUpModal({ onClose, onSuccess }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkReady, setPkReady] = useState(false);
  const pkOpts = useRef(null);

  // Pre-fetch ONE step-up challenge when the modal opens so the passkey tap can
  // call get() synchronously (iOS-safe). Falls back to password if unavailable.
  useEffect(() => {
    if (!passkeySupported()) return;
    let alive = true;
    (async () => {
      try { const o = await getStepUpOptions(); if (alive) { pkOpts.current = o; setPkReady(true); } }
      catch { /* password path stays available */ }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async () => {
    setBusy(true);
    try { await api.post("/auth/step-up/password", { password }); onSuccess(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Incorrect"); }
    finally { setBusy(false); }
  };
  const viaPasskey = () => {
    setBusy(true);
    getAssertion(pkOpts.current.publicKey)
      .then((a) => finishStepUp(pkOpts.current.tx, a))
      .then(() => onSuccess())
      .catch((e) => { toast.error(e.response?.data?.detail || e.message || "Passkey check failed"); setBusy(false); });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="stepup-modal">
      <div className="sg-card p-6 w-full max-w-sm sg-fade">
        <div className="flex items-center justify-between mb-3">
          <div className="font-head text-lg font-bold flex items-center gap-2"><ShieldCheck size={18} style={{ color: "var(--accent)" }} /> Confirm it's you</div>
          <button onClick={onClose} className="sg-btn" style={{ padding: 6 }} data-testid="stepup-close"><X size={16} /></button>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>For your security, re-enter your password before changing security settings.</p>
        <PasswordInput className="mb-3" placeholder="Current password" value={password} data-testid="stepup-password" autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <button className="sg-btn sg-btn-accent w-full mb-2" disabled={busy} onClick={submit} data-testid="stepup-submit">
          {busy ? <Loader2 className="animate-spin" size={16} /> : "Confirm"}
        </button>
        {passkeySupported() && (
          <button className="sg-btn w-full" disabled={busy || !pkReady} onClick={viaPasskey} data-testid="stepup-passkey-btn"><Fingerprint size={16} /> Use a passkey instead</button>
        )}
      </div>
    </div>
  );
}

function RecoveryCodes({ codes, onDone }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="recovery-codes">
      <div className="sg-card p-6 w-full max-w-md sg-fade">
        <div className="font-head text-lg font-bold mb-2">Save your recovery codes</div>
        <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>Each code works once. This is the only time they're shown. Store them somewhere safe.</p>
        <pre className="sg-card-elev p-3 text-sm select-all mb-4" style={{ whiteSpace: "pre-wrap" }} data-testid="recovery-codes-list">{codes.join("\n")}</pre>
        <div className="flex gap-2">
          <button className="sg-btn" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Copied"); }}><Copy size={16} /> Copy</button>
          <button className="sg-btn sg-btn-accent flex-1" onClick={onDone} data-testid="recovery-done-btn">I've saved them</button>
        </div>
      </div>
    </div>
  );
}

// iOS-safe "Add passkey" dialog. Options are fetched up front (before any
// biometric). When the user taps Continue, navigator.credentials.create() is
// invoked synchronously in the handler so Safari keeps its transient user
// activation and Face ID / Touch ID can launch.
function AddPasskeyModal({ onClose, onAdded, requestStepUp }) {
  const [nickname, setNickname] = useState("My device");
  const [phase, setPhase] = useState("loading"); // loading | ready | creating | error
  const [errMsg, setErrMsg] = useState("");
  const opts = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const o = await getRegisterOptions();
        if (!alive) return;
        opts.current = o;
        setPhase("ready");
      } catch (e) {
        if (!alive) return;
        setErrMsg(apiError(e.response?.data?.detail) || "Couldn't start passkey setup. Please try again.");
        setPhase("error");
      }
    })();
    return () => { alive = false; };
  }, []);

  const finish = async (credential, tx, nick) => {
    try {
      await finishRegisterPasskey(tx, nick, credential);
      toast.success("Passkey added");
      await onAdded();
    } catch (e) {
      if (e.response?.status === 401) {
        // Biometric already succeeded; server just needs a fresh step-up.
        // Close this dialog, re-verify identity, then retry finish with the
        // SAME challenge + credential (no second Face ID prompt).
        onClose();
        requestStepUp(async () => {
          await finishRegisterPasskey(tx, nick, credential);
          toast.success("Passkey added");
          await onAdded();
        });
        return;
      }
      toast.error(apiError(e.response?.data?.detail) || "Could not save passkey. Please try again.");
      setPhase("ready");
    }
  };

  const onContinue = () => {
    // Keep everything before create() synchronous — no await here.
    const nick = (nickname || "Passkey").trim() || "Passkey";
    const { pk, tx } = opts.current;
    setPhase("creating");
    createRegistrationCredential(pk)
      .then((credential) => finish(credential, tx, nick))
      .catch((e) => {
        const map = {
          NotAllowedError: "Face ID / Touch ID was cancelled or timed out. Tap Continue and approve the prompt.",
          SecurityError: "Your browser blocked the passkey — it must run over HTTPS on the correct domain.",
          InvalidStateError: "A passkey for this device is already registered on your account.",
          AbortError: "Passkey setup was interrupted. Please try again.",
          NotSupportedError: "This device can't create a passkey here.",
        };
        toast.error(map[e?.name] || e?.message || "Could not add passkey");
        setPhase("ready");
      });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="add-passkey-modal">
      <div className="sg-card p-6 w-full max-w-sm sg-fade">
        <div className="flex items-center justify-between mb-3">
          <div className="font-head text-lg font-bold flex items-center gap-2"><Fingerprint size={18} style={{ color: "var(--accent)" }} /> Add a passkey</div>
          <button onClick={onClose} className="sg-btn" style={{ padding: 6 }} data-testid="add-passkey-close"><X size={16} /></button>
        </div>

        {phase === "loading" && (
          <div className="flex items-center gap-2 text-sm py-4" style={{ color: "var(--txt2)" }} data-testid="add-passkey-loading">
            <Loader2 className="animate-spin" size={16} /> Preparing…
          </div>
        )}

        {phase === "error" && (
          <div data-testid="add-passkey-error">
            <p className="text-sm mb-4" style={{ color: "#EF4444" }}>{errMsg}</p>
            <button className="sg-btn w-full" onClick={onClose} data-testid="add-passkey-error-close">Close</button>
          </div>
        )}

        {(phase === "ready" || phase === "creating") && (
          <>
            <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>
              Give this device a name, then tap Continue and approve with Face ID, Touch ID or your device PIN.
            </p>
            <input className="sg-input mb-3" placeholder="e.g. iPhone Face ID" value={nickname} data-testid="add-passkey-name"
              disabled={phase === "creating"} onChange={(e) => setNickname(e.target.value)} />
            <button className="sg-btn sg-btn-accent w-full" onClick={onContinue} disabled={phase !== "ready"} data-testid="add-passkey-continue">
              {phase === "creating"
                ? (<><Loader2 className="animate-spin" size={16} /> Waiting for Face ID…</>)
                : (<><Fingerprint size={16} /> Continue</>)}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SecurityCenter() {
  const [status, setStatus] = useState(null);
  const [passkeys, setPasskeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null); // {qr_data_url, manual_key}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [stepUp, setStepUp] = useState(false);
  const [addPk, setAddPk] = useState(false);
  const pending = useRef(null);

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/auth/mfa/status"), api.get("/auth/passkeys")]);
    setStatus(s.data); setPasskeys(p.data);
  };
  useEffect(() => { load(); }, []);

  // Run a sensitive action; if backend demands step-up (401), open modal and retry.
  const sensitive = async (fn) => {
    try { await fn(); }
    catch (e) {
      if (e.response?.status === 401) { pending.current = fn; setStepUp(true); }
      else toast.error(apiError(e.response?.data?.detail) || e.message || "Failed");
    }
  };
  const onStepUpSuccess = async () => {
    setStepUp(false);
    const fn = pending.current; pending.current = null;
    if (fn) { try { await fn(); } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); } }
  };
  const requestStepUp = (fn) => { pending.current = fn; setStepUp(true); };

  const addPasskey = () => setAddPk(true);

  const revokePasskey = (cid) => sensitive(async () => {
    await api.delete(`/auth/passkeys/${cid}`); toast.success("Passkey removed"); await load();
  });
  const renamePasskey = async (cid) => {
    const name = window.prompt("Rename passkey", "");
    if (!name) return;
    try { await api.patch(`/auth/passkeys/${cid}`, { nickname: name }); await load(); } catch (e) { toast.error("Failed"); }
  };

  const beginEnroll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/activate", { code }); setSetup(null); setCode(""); setRecovery(data.recovery_codes); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Invalid code"); } finally { setBusy(false); }
  };
  const disable2fa = () => sensitive(async () => { await api.post("/auth/mfa/disable"); toast.success("Two-factor disabled"); await load(); });
  const regen = () => sensitive(async () => { const { data } = await api.post("/auth/mfa/recovery/regenerate"); setRecovery(data.recovery_codes); await load(); });

  if (status === null) return <Loading label="Loading security center" />;

  return (
    <div className="max-w-3xl" data-testid="security-page">
      <SectionTitle sub="Manage your sign-in methods, two-factor authentication and recovery">Account Security</SectionTitle>

      {/* Passkeys */}
      <div className="sg-card p-6 mb-6" data-testid="passkeys-section">
        <div className="flex items-center justify-between mb-3">
          <div className="font-head text-xl font-bold flex items-center gap-2"><Fingerprint size={20} style={{ color: "var(--accent)" }} /> Passkeys</div>
          <button className="sg-btn sg-btn-accent" onClick={addPasskey} disabled={busy || !passkeySupported()} data-testid="add-passkey-btn"><Plus size={16} /> Add passkey</button>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>
          Sign in with Face ID, Touch ID, Windows Hello, your device PIN or a security key. Biometrics never leave your device.
          {!passkeySupported() && " (This browser doesn't support passkeys.)"}
        </p>
        {passkeys.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="no-passkeys">No passkeys yet.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {passkeys.map((p) => (
              <div key={p.credential_id} className="sg-card-elev p-3 flex items-center justify-between" data-testid={`passkey-row-${p.credential_id}`}>
                <div className="flex items-center gap-3">
                  <Smartphone size={18} style={{ color: "var(--txt2)" }} />
                  <div>
                    <div className="font-semibold text-sm">{p.nickname}</div>
                    <div className="text-xs" style={{ color: "var(--txt2)" }}>
                      Added {new Date(p.created_at).toLocaleDateString()}{p.last_used_at ? ` · last used ${new Date(p.last_used_at).toLocaleDateString()}` : " · never used"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="sg-btn" style={{ padding: 8 }} onClick={() => renamePasskey(p.credential_id)} data-testid={`rename-passkey-${p.credential_id}`}><KeyRound size={14} /></button>
                  <button className="sg-btn" style={{ padding: 8 }} onClick={() => revokePasskey(p.credential_id)} data-testid={`revoke-passkey-${p.credential_id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Two-Factor */}
      <div className="sg-card p-6 mb-6" data-testid="twofa-section">
        <div className="flex items-center justify-between mb-3">
          <div className="font-head text-xl font-bold flex items-center gap-2"><ShieldCheck size={20} style={{ color: "var(--accent)" }} /> Two-Factor Authentication</div>
          <span className="chip" style={{ background: status.enabled ? "rgba(34,197,94,0.15)" : "rgba(148,163,184,0.15)", color: status.enabled ? "#22C55E" : "var(--txt2)", borderColor: status.enabled ? "#22C55E" : "var(--brd)" }} data-testid="twofa-status">
            {status.enabled ? "Enabled" : "Off"}
          </span>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>Use an authenticator app (Google Authenticator, Authy, 1Password) for a one-time code at sign-in.</p>

        {!status.enabled && !setup && (
          <button className="sg-btn sg-btn-accent" onClick={beginEnroll} disabled={busy} data-testid="enable-2fa-btn"><Plus size={16} /> Enable 2FA</button>
        )}

        {setup && (
          <div className="sg-card-elev p-4" data-testid="twofa-enroll">
            <p className="text-sm mb-3">Scan this QR code with your authenticator app, then enter the 6-digit code.</p>
            <img src={setup.qr_data_url} alt="2FA QR code" className="rounded-lg bg-white p-2 mb-3" style={{ width: 180, height: 180 }} data-testid="twofa-qr" />
            <div className="text-xs mb-3" style={{ color: "var(--txt2)" }}>Manual key: <code className="select-all" data-testid="twofa-manual-key">{setup.manual_key}</code></div>
            <div className="flex gap-2">
              <input className="sg-input" inputMode="numeric" maxLength={6} placeholder="123456" value={code} data-testid="twofa-code"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
              <button className="sg-btn sg-btn-accent" onClick={activate} disabled={busy || code.length !== 6} data-testid="twofa-activate-btn">Verify &amp; activate</button>
            </div>
          </div>
        )}

        {status.enabled && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm" style={{ color: "var(--txt2)" }}>{status.recovery_remaining} recovery codes remaining</span>
            <div className="flex-1" />
            <button className="sg-btn" onClick={regen} data-testid="regen-recovery-btn"><RefreshCw size={16} /> Regenerate recovery codes</button>
            <button className="sg-btn" onClick={disable2fa} data-testid="disable-2fa-btn" style={{ color: "#EF4444" }}><Trash2 size={16} /> Disable 2FA</button>
          </div>
        )}
      </div>

      {stepUp && <StepUpModal onClose={() => { setStepUp(false); pending.current = null; }} onSuccess={onStepUpSuccess} />}
      {addPk && <AddPasskeyModal onClose={() => setAddPk(false)} onAdded={async () => { await load(); setAddPk(false); }} requestStepUp={requestStepUp} />}
      {recovery && <RecoveryCodes codes={recovery} onDone={() => setRecovery(null)} />}
    </div>
  );
}
